import classNames from 'classnames';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import React, { FC, useCallback, useMemo } from 'react';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { LibraryHoverableCardSmall } from 'src/common/components/library/hoverable-content-card-small';
import { DataTable } from 'src/common/components/table/data-table';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { IconButton } from 'src/common/icon-button';
import { Journey } from 'src/common/interfaces/journey.interface';
import { Node } from 'src/common/interfaces/node.interface';
import ChevronUpStrongIcon from 'src/monet/icons/ChevronUpStrongIcon';
import { formatDuration } from 'src/utils/date';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { formatNumber } from 'src/utils/text';
import { useJourneyDocumentInsights } from '../use-journey-document-insights.hook';
import { useJourneyInsightsByStep } from '../use-journey-insights-by-step.hook';
import { DashboardInsightsJourneysJourneyVisitorDocumentInsights } from './document-insights';

type Props = {
  journey: Journey;
  contentList: Node[];
  sessionUuid: string;
  disableSessionGrouping?: boolean;
};

type EditorInsightsStepsListHeaderTypes = 'number' | 'step_name' | 'view_count' | 'total_time';

type EditorInsightsStepsListHeaderSortType = TableSortableColumnHeaderSortType<EditorInsightsStepsListHeaderTypes>;

type StepInsightItem = {
  node: Node;
  step_index: number;
  step_name: string;
  view_count: number;
  total_time: number;
  expandable: boolean;
};

export const DashboardInsightsJourneysJourneyVisitorStepsTable: FC<Props> = ({
  contentList,
  journey,
  sessionUuid,
  disableSessionGrouping = false,
}) => {
  const {
    filterBarParams: { search_term, sort_by, sort_order },
    updateSort,
  } = useFilterBarParams({ dontDebounceSearch: true, sort_by: 'number', sort_order: 'asc' });

  const onTableHeaderClick = useCallback(
    (sortStack: EditorInsightsStepsListHeaderSortType) => {
      updateSort(sortStack.sortName, sortStack.orderBy);
    },
    [updateSort]
  );

  const insightsFilters = useMemo(
    () => ({ session_uuid: sessionUuid, disable_session_grouping: disableSessionGrouping }),
    [sessionUuid, disableSessionGrouping]
  );
  const { fetching, stepInsights } = useJourneyInsightsByStep({
    journeyUuId: journey?.uuid,
    filters: insightsFilters,
  });
  const documentInsightsParams = useMemo(
    () => ({
      journeyUuId: journey.uuid,
      nodeIds: contentList.map((node) => node.id),
      filters: { session_uuid: sessionUuid, disable_session_grouping: disableSessionGrouping },
    }),
    [journey, contentList, sessionUuid, disableSessionGrouping]
  );
  const { fetching: fetchingDocumentInsights, documentInsights } = useJourneyDocumentInsights(documentInsightsParams);

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'number'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='number'
            onClick={onTableHeaderClick}
            title='No'
            tooltip='Step order within Journey'
          />
        ),
        accessor: 'number',
        width: '6%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const { step_index } = row.original as StepInsightItem;
          return <div {...(row.original.expandable && row.getToggleRowExpandedProps())}>{step_index + 1}</div>;
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'step_name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='step_name'
            onClick={onTableHeaderClick}
            title='Step'
            tooltip='Step name within Journey'
          />
        ),
        accessor: 'step_name',
        width: '39%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const node = row.original.node as Node;
          return (
            <div
              className='flex gap-4 items-center'
              {...(row.original.expandable && row.getToggleRowExpandedProps())}
              style={{ wordBreak: 'break-word' }}
            >
              <LibraryHoverableCardSmall content={node} hidePreview={node?.type?.name === 'neue'} />
            </div>
          );
        },
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'view_count'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='view_count'
            onClick={onTableHeaderClick}
            title='Views'
            tooltip='Total times this step was viewed'
          />
        ),
        accessor: 'view_count',
        width: '13%',
        Cell: ({ row, index }: { row: any; index: number }) => (
          <div
            className='text-bedrock-p text-bedrock-black'
            {...(row.original.expandable && row.getToggleRowExpandedProps())}
          >
            {formatNumber(row.original.view_count)}
          </div>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'total_time'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='total_time'
            onClick={onTableHeaderClick}
            title='Total time'
            tooltip='Total time spent viewing this step'
          />
        ),
        accessor: 'total_time',
        width: '13%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const { total_time } = row.original as StepInsightItem;
          return (
            <div
              className='text-bedrock-p text-bedrock-black'
              {...(row.original.expandable && row.getToggleRowExpandedProps())}
            >
              {total_time > 0 ? formatDuration(total_time) : '-'}
            </div>
          );
        },
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: '29%',
        allowShrinking: true,
        Cell: ({ row }: { row: any }) => {
          if (!row.original.expandable) {
            return null;
          }
          return (
            <div
              className={classNames(
                'mr-2 flex justify-end items-center transition group-hover:opacity-100',
                row.isExpanded ? 'opacity-100' : 'opacity-0'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <div {...(row.original.expandable && row.getToggleRowExpandedProps())}>
                <IconButton>
                  <ChevronUpStrongIcon
                    className={classNames('transition-transform', {
                      '-rotate-180': !row.isExpanded,
                    })}
                  />
                </IconButton>
              </div>
            </div>
          );
        },
      }),
    ],
    [sort_by, sort_order, onTableHeaderClick]
  );

  const stepInsightData: StepInsightItem[] = useMemo(() => {
    if (fetching) {
      return [];
    }

    let stepData = contentList.map((node, index) => {
      let data = {
        node,
        step_name: node.name,
        step_index: index,
        view_count: 0,
        total_time: 0,
        expandable: false,
      };
      const contentInsight = stepInsights.find((si) => si.node_id === node.id);
      if (!contentInsight) {
        return data;
      }
      const { num_sessions, total_time } = contentInsight;
      data = {
        ...data,
        view_count: num_sessions,
        total_time: Math.ceil(total_time),
      };

      return {
        ...data,
        expandable: node.content_type === 'pdf' && data.view_count > 0 && data.total_time > 0,
      };
    });

    if (search_term) {
      stepData = stepData.filter((d) => d.step_name.toLowerCase().includes(search_term.toLowerCase()));
    }

    if (sort_by) {
      stepData = sortBy(stepData, [
        (v) =>
          sort_by === 'step_name'
            ? v.step_name
            : sort_by === 'view_count'
            ? v.view_count
            : sort_by === 'total_time'
            ? v.total_time
            : sort_by === 'number'
            ? v.step_index
            : null,
      ]);
      if (sort_order === 'desc') {
        stepData = reverse(stepData);
      }
    }

    return stepData;
  }, [contentList, search_term, sort_by, sort_order, fetching, stepInsights]);

  const renderRowSubComponent = useCallback(
    ({ row }) => {
      const item = row.original as StepInsightItem;
      if (!item.expandable) {
        return null;
      }
      const nodeDocumentInsights = documentInsights.filter((di) => di.node_id === item.node.id);
      if (!nodeDocumentInsights || nodeDocumentInsights.length === 0) {
        return null;
      }
      return (
        <DashboardInsightsJourneysJourneyVisitorDocumentInsights
          node={item.node}
          documentInsights={nodeDocumentInsights}
        />
      );
    },
    [documentInsights]
  );

  if (fetching || fetchingDocumentInsights) {
    return <FixedLoadingSpinner />;
  }

  if (stepInsightData.length === 0) {
    return <div className='text-bedrock-p'>No step views found</div>;
  }
  return (
    <DataTable
      columns={columns}
      data={stepInsightData}
      tableClassName='w-full'
      rowAlignTop={false}
      expandAllRowsByDefault
      disableRowCursorPointer={(row) => !(row.original as StepInsightItem).expandable}
      renderRowSubComponent={renderRowSubComponent}
    />
  );
};
